<template>
  <aside class="user-switch-wrapper" :class="{ active: popupIsVisible }">
    <b-button
      variant="transparent"
      class="border-none user-switch-toggle-btn"
      data-cy="toggle-user-change-menu"
      aria-controls="user-switch-list"
      :aria-expanded="popupIsVisible"
      @click="hide"
    >
      <sdui-icon class="icon-chevron-left" />
      <span class="sr-only">{{ popupIsVisible ? $t('global.header.multiUser.close') : $t('global.header.multiUser.open') }}</span>
    </b-button>

    <nav class="user-switch-nav"
         data-cy="user-switch-nav"
         :aria-label="$t('ui.navigation')"
    >
      <ul id="user-switch-list" class="list-unstyled m-0">
        <li v-for="(child, i) in users"
            :key="'child' + i"
            class="p-3"
            :data-cy="activeUserId === child.id ? 'active' : 'inactive'"
        >
          <b-button
            variant="transparent"
            class="user-switch-btn"
            :class="{ active: activeUserId === child.id }"
            :data-cy="`child-switch-${child.id}`"
            @click="switchUser(child.id)"
          >
            <span class="d-block w-100 lh-1">
              <span v-if="child.meta" class="mb-0 title">{{ child.meta.displayname }}</span>
              <span v-if="child.school" class="small subtitle">{{ child.school.name }}</span>
            </span>
          </b-button>
        </li>
        <li v-if="canEnvironments" class="py-1">
          <b-button variant="transparent"
                    class="environment-switch-btn"
                    @click="showEnvironments = true"
          >
            <span class="environment-switch-btn--icon"><sdui-icon class="icon-school" /></span>
            <p class="mb-0">{{ $t('layout.user_change_modal.environments') }}</p>
          </b-button>
        </li>
        <cypress-user-switch v-if="enableAccountSwitching" />
        <li class="py-1">
          <b-button variant="transparent"
                    class="add-account-btn"
                    @click="$router.push({ name: 'settings-multi-users' })"
          >
            <span class="add-account-btn--icon"><sdui-icon class="icon-user-settings" /></span>
            <p class="mb-0">{{ $t('layout.user_change_modal.manage_accounts') }}</p>
          </b-button>
        </li>
      </ul>
    </nav>
    <SModal v-model="showEnvironments"
            :header-title="$t('development.choose_environment')"
            hide-footer
    >
      <switch-environment v-if="showEnvironments" />
    </SModal>
  </aside>
</template>

<script>
import CypressUserSwitch from '../components/management/CypressUserSwitch.vue'
import {defineAsyncComponent} from 'vue'

export default {
  name: 'UserChangeModal',
  components: {
    SwitchEnvironment: defineAsyncComponent(() => import('src/components/management/SwitchEnvironment.vue')),
    CypressUserSwitch
  },

  props: {
    isVisible: {
      type: Boolean,
    },

    enableAccountSwitching: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    showEnvironments: false,
    activeUserId: null,
  }),

  computed: {
    popupIsVisible: {
      get: function () {
        return this.isVisible
      },

      set: function (newValue) {
        this.$emit('hideModal', newValue)
      },
    },

    users() {
      return Object.values(this.$store.state.multiUsers.users)
    },

    hasAdmin() {
      return !!this.$store.getters['multiUsers/adminAuthToken']
    },

    canEnvironments() {
      const slinksWithEnvSwitch = [import.meta.env.VITE_SLINK_SDUI_GMBH_INTERNAL_SCHOOL, import.meta.env.VITE_SLINK_MUSTERSCHULE_KOBLENZ]
      return JSON.parse(import.meta.env.VITE_BETA_FEATURES) || slinksWithEnvSwitch.includes(this.$me?.school?.slink)
    },
  },

  emits: ['hideModal'],

  methods: {
    async switchUser(targetId) {
      const isCurrentUser = targetId === this.$me?.id
      if (isCurrentUser) return
      this.activeUserId = targetId
      await this.$sleep(100) // sleep shortly, so we can display the target user as active (feedback for user click)
      this.selectUser(targetId)
    },

    selectUser(userId, newUri) {
      const payload = newUri
        ? {
          userId,
          path: newUri,
        }
        : { userId }
      this.$store.dispatch('multiUsers/selectUser', payload, { root: true })
    },

    hide() {
      this.popupIsVisible = !this.popupIsVisible
      this.$emit('hideModal', this.popupIsVisible)
    },

    switchSchool() {
      this.$router.push({ name: 'settings-user', params: { id: this.$me.id }, query: { modal: 'changeSchool' } })
    },

    keyboardSwitch(event) {
      if (this.users.length < 2) return
      // select keyboard 0-10 Number
      const userIndex = event.which - 48
      if (event.altKey && event.which === 83 && this.hasAdmin) {
        if (this.$me.school.slink === import.meta.env.VITE_SLINK_SDUI_GMBH_SCHOOL) {
          return this.$toastNotify({
            type: 'error',
            text: this.$t('layout.user_change_modal.notifications.already_in_management_school'),
          })
        }
        if ([22231].includes(this.$me.school_id)) {
          return this.$toastNotify({
            type: 'error',
            text: this.$t('layout.user_change_modal.notifications.in_internal_school'),
          })
        }
        return this.switchSchool()
      }

      if (event.altKey && event.which === 65 && this.hasAdmin) {
        const editSchoolPath = this.$router.resolve({
          name: 'edit-school',
          params: { schoolUuid: this.$me.school.uuid },
          query: { showSchoolSearch: 'true' },
        }).href
        const adminUser = this.$store.getters['multiUsers/adminUser']
        this.selectUser(adminUser.id, editSchoolPath)
      }

      if (!event.altKey || userIndex < 0 || userIndex > this.users.length) return

      const user = this.users[userIndex - 1]
      console.log({ userIndex })

      if (user.id === this.$me.id) {
        return this.$toastNotify({
          type: 'success',
          text: this.$t('layout.user_change_modal.notifications.account_exists'),
        })
      }

      this.selectUser(user.id)
    },
  },

  mounted() {
    this.activeUserId = this.$me?.id
  },

  created() {
    document.addEventListener('keydown', this.keyboardSwitch)
  },

  unmounted() {
    document.removeEventListener('keydown', this.keyboardSwitch)
  },
}
</script>

<style scoped lang="scss">
// only change outline-color when menu is activated, otherwise it's hard to see (white on white)
.keyboard-navigation .user-switch-wrapper.active button:focus-visible {
  outline-color: $color-white !important;
}

.user-switch-wrapper {
  display: flex;
  align-items: center;
  height: 100vh;
  position: fixed;
  z-index: 1039 !important;
  right: 0;
  top: 0;
  pointer-events: none;

  &.active {
    transform: none;
    z-index: 1041 !important;

    .user-switch-toggle-btn {
      .icon {
        transform: rotate(180deg);
      }
    }

    .user-switch-nav {
      visibility: visible;
      width: min(15.625rem, 80vw);
      padding: 6px;
    }
  }
}

.user-switch-nav {
  visibility: hidden;
  width: 0;
  height: auto;
  background-color: $color-primary;
  border-radius: 21px 0 0 21px;
  max-height: 400px;
  min-height: 150px;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  transition: 0.3s;
  pointer-events: auto;
}

.user-switch-toggle-btn {
  margin-right: -1px;
  width: 40px;
  height: 124px;
  position: relative;
  background-image: url('/img/miscellaneous/ui/notch-left.svg') !important;
  background-size: contain;
  background-position: center right;
  background-repeat: no-repeat;
  background-color: transparent !important;
  filter: var(--primary-svg-filter);
  pointer-events: auto;

  .icon {
    font-size: pxtorem(18) !important;
    color: $color-white;
    transition: 0.3s;
  }
}

.user-switch-btn,
.environment-switch-btn,
.add-account-btn {
  // !important styles to override bootstrap button
  border-radius: 5px !important;
  height: 3.3rem !important;
  text-align: left !important;
  width: 100%;
  display: flex !important;
  flex-direction: column;
  justify-content: center;
  padding: 12px !important;
  color: $color-primary-light !important;
  font-weight: $font-weight-normal !important;

  .title,
  .subtitle {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    display: inline-block;
  }

  .title {
    font-size: pxtorem(15);
    line-height: pxtorem(18);
  }

  .subtitle {
    opacity: 0.7;
  }

  &.active {
    color: $color-primary !important;
    background-color: $color-primary-light;
  }
}

.environment-switch-btn,
.add-account-btn {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  padding: 15px 0 15px 12px !important;

  .environment-switch-btn--icon,
  .add-account-btn--icon {
    color: $color-primary;
    display: flex;

    .icon {
      background-color: $color-white;
      border-radius: 50%;
      cursor: pointer;
      height: 28px;
      width: 28px;
      position: relative;

      &::before {
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        position: absolute;
      }
    }
  }
}
</style>
